import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { Loader2, Building2, CreditCard, FileText, LogOut, PoundSterling, Ticket, Plus, Trash2, Newspaper, LogIn, GripVertical, CheckCircle2, Users, ShieldCheck, Pin, X, Pencil, Mic2, Receipt, MapPin, Mail } from "lucide-react";
import { toast } from "../hooks/use-toast";
import ImageUpload from "../components/ImageUpload";
import { slugify } from "../lib/slugify";
import AdminOrdersTab from "./AdminOrdersTab";
import AdminTicketsTab from "./AdminTicketsTab";
import AdminEmailsTab from "./AdminEmailsTab";

const FIELD_META = {
  favicon: { label: "Favicon (browser tab icon)", desc: "The small icon shown in browser tabs and bookmarks. Use a square image (PNG/JPG), at least 64×64px." },
  og_image: { label: "Social sharing image (Open Graph)", desc: "Preview image shown when a link to your site is shared on WhatsApp, X, Facebook, LinkedIn, etc. Recommended 1200×630px." },
  site_logo: { label: "Site logo", desc: "Shown in the site header and on your emails." },
  login_hero_image: { label: "Login page image" },
  meta_title: { label: "Browser / SEO title" },
  meta_description: { label: "SEO meta description" },
};

const CONTENT_GROUPS = [
  { title: "SEO & page title", keys: ["meta_title", "meta_description"] },
  { title: "Header", keys: ["header_cta", "header_cta_link"] },
  { title: "Branding, favicon & social image", keys: ["site_logo", "login_hero_image", "favicon", "og_image"] },
  { title: "Hero", keys: ["hero_title", "hero_subtitle", "hero_cta", "hero_cta_link", "hero_image_envelope", "hero_image_phone"] },
  { title: "Storefront hero", keys: ["stores_title", "stores_subtitle", "stores_cta"] },
  { title: "Video messages", keys: ["video_title", "video_text", "video_cta", "video_image"] },
  { title: "Admin tools", keys: ["admin_title", "admin_text", "admin_cta", "admin_quote", "admin_quote_name", "admin_quote_role", "admin_quote_avatar"] },
  { title: "Fulfilment", keys: ["fulfil_title", "fulfil_text", "fulfil_cta", "fulfil_image_1", "fulfil_image_2", "fulfil_image_3"] },
  { title: "Support", keys: ["support_title", "support_text", "support_cta", "support_quote", "support_quote_name", "support_quote_role", "support_quote_avatar"] },
  { title: "Integrations", keys: ["integ_title", "integ_text", "integ_cta"] },
  { title: "Grow CTA", keys: ["grow_title", "grow_subtitle"] },
  { title: "Blog", keys: ["blog_title"] },
  { title: "Footer", keys: ["footer_tagline"] },
];

export default function AdminPanel() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("businesses");
  const [businesses, setBusinesses] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [fees, setFees] = useState(null);
  const [revFilter, setRevFilter] = useState("");
  const [content, setContent] = useState(null);
  const [articles, setArticles] = useState(null);
  const [payoutReqs, setPayoutReqs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [ownFees, setOwnFees] = useState(null);
  const [eventFeeBiz, setEventFeeBiz] = useState(null);
  const [eventFeeList, setEventFeeList] = useState([]);
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [cities, setCities] = useState([]);
  const [newCity, setNewCity] = useState({ name: "", lat: "", lng: "" });
  const [venues, setVenues] = useState([]);
  const [venueCityFilter, setVenueCityFilter] = useState("all");
  const [newVenue, setNewVenue] = useState({ name: "", city: "", address: "", capacity: "" });
  const [flags, setFlags] = useState([]);
  const [testSms, setTestSms] = useState("");
  const [testSmsSending, setTestSmsSending] = useState(false);
  const [staffVenue, setStaffVenue] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "" });
  const [acts, setActs] = useState([]);
  const [venueModal, setVenueModal] = useState(null);
  const [venueBusy, setVenueBusy] = useState(false);

  const loadPayouts = () => api.get("/admin/payout-requests").then(({ data }) => setPayoutReqs(data)).catch(() => {});
  const loadInvoices = () => api.get("/admin/invoices").then(({ data }) => setInvoices(data)).catch(() => {});
  const loadOwnFees = () => api.get("/admin/own-stripe-fees").then(({ data }) => setOwnFees(data)).catch(() => {});

  useEffect(() => {
    if (user?.role === "super_admin") {
      const fullAdmin = user.staff_role !== "business_manager";
      api.get("/admin/businesses").then(({ data }) => setBusinesses(data));
      api.get("/admin/fees").then(({ data }) => setFees(data)).catch(() => {});
      api.get("/site-content").then(({ data }) => setContent(data));
      api.get("/articles").then(({ data }) => setArticles(data));
      loadPayouts();
      loadInvoices();
      loadOwnFees();
      api.get("/admin/event-flags").then(({ data }) => setFlags(data || [])).catch(() => {});
      if (fullAdmin) {
        api.get("/admin/platform").then(({ data }) => setPlatform(data)).catch(() => {});
        api.get("/admin/categories").then(({ data }) => setCats(data || [])).catch(() => {});
        api.get("/cities").then(({ data }) => setCities(Array.isArray(data) ? data : [])).catch(() => {});
        api.get("/venues").then(({ data }) => setVenues(Array.isArray(data) ? data : [])).catch(() => {});
        api.get("/admin/acts").then(({ data }) => setActs(Array.isArray(data) ? data : [])).catch(() => {});
      }
    }
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={40} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "venue") return <Navigate to="/venue" replace />;
  if (user.role !== "super_admin") return <Navigate to="/dashboard" replace />;

  const isFullAdmin = user.staff_role !== "business_manager";

  const savePlatform = async () => {
    await api.put("/admin/platform", platform);
    toast({ title: "Platform settings saved" });
  };
  const saveCats = async (list) => {
    try {
      const { data } = await api.put("/admin/categories", { categories: list });
      setCats(data.categories); setNewCat("");
      toast({ title: "Categories updated" });
    } catch { toast({ title: "Error", description: "Could not save categories", variant: "destructive" }); }
  };
  const addCat = () => { const n = newCat.trim(); if (n && !cats.some((c) => c.toLowerCase() === n.toLowerCase())) saveCats([...cats, n]); };
  const removeCat = (c) => saveCats(cats.filter((x) => x !== c));
  const saveCities = async (list) => {
    try {
      const { data } = await api.put("/admin/cities", { cities: list });
      setCities(data.cities); setNewCity({ name: "", lat: "", lng: "" });
      toast({ title: "Cities updated" });
    } catch { toast({ title: "Error", description: "Could not save cities", variant: "destructive" }); }
  };
  const addCity = () => {
    const n = newCity.name.trim();
    if (!n || cities.some((c) => (c.name || "").toLowerCase() === n.toLowerCase())) return;
    const entry = { name: n };
    if (String(newCity.lat).trim() !== "") entry.lat = parseFloat(newCity.lat);
    if (String(newCity.lng).trim() !== "") entry.lng = parseFloat(newCity.lng);
    saveCities([...cities, entry]);
  };
  const removeCity = (name) => saveCities(cities.filter((c) => c.name !== name));

  const loadVenues = () => api.get("/venues").then(({ data }) => setVenues(Array.isArray(data) ? data : [])).catch(() => {});
  const addVenueAdmin = async () => {
    const name = newVenue.name.trim();
    if (!name) return;
    try {
      await api.post("/admin/venues", { name, city: newVenue.city.trim(), address: newVenue.address.trim(), capacity: parseInt(newVenue.capacity) || 0 });
      setNewVenue({ name: "", city: "", address: "", capacity: "" });
      loadVenues();
      toast({ title: "Venue added" });
    } catch { toast({ title: "Error", description: "Could not add venue", variant: "destructive" }); }
  };
  const removeVenue = async (id) => {
    try { await api.delete(`/admin/venues/${id}`); loadVenues(); toast({ title: "Venue removed" }); }
    catch { toast({ title: "Error", description: "Could not remove venue", variant: "destructive" }); }
  };
  const sendTestSms = async () => {
    const to = testSms.trim();
    if (!to) { toast({ title: "Enter a mobile number", variant: "destructive" }); return; }
    setTestSmsSending(true);
    try {
      await api.post("/admin/send-test-sms", { to });
      toast({ title: "Test SMS sent", description: `Check ${to} for the message.` });
    } catch (e) {
      toast({ title: "Could not send", description: e?.response?.data?.detail || "SMS failed", variant: "destructive" });
    } finally { setTestSmsSending(false); }
  };
  const openStaff = async (v) => {
    setStaffVenue(v); setStaffList([]); setNewStaff({ name: "", email: "", password: "" });
    try { const { data } = await api.get(`/admin/venues/${v.id}/staff`); setStaffList(data || []); }
    catch { toast({ title: "Error", description: "Could not load staff", variant: "destructive" }); }
  };
  const addStaff = async () => {
    if (!staffVenue) return;
    try {
      await api.post(`/admin/venues/${staffVenue.id}/staff`, { name: newStaff.name.trim(), email: newStaff.email.trim(), password: newStaff.password });
      setNewStaff({ name: "", email: "", password: "" });
      const { data } = await api.get(`/admin/venues/${staffVenue.id}/staff`); setStaffList(data || []);
      toast({ title: "Door-staff login added" });
    } catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Could not add staff", variant: "destructive" }); }
  };
  const removeStaff = async (uid) => {
    if (!staffVenue) return;
    try {
      await api.delete(`/admin/venues/${staffVenue.id}/staff/${uid}`);
      setStaffList((l) => l.filter((s) => s.id !== uid));
      toast({ title: "Login removed" });
    } catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Could not remove", variant: "destructive" }); }
  };
  const resetStaff = async (uid, email) => {
    const password = window.prompt(`New password for ${email}:`);
    if (!password) return;
    try { await api.post(`/admin/venues/${staffVenue.id}/staff/${uid}/reset`, { password }); toast({ title: "Password reset" }); }
    catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Could not reset", variant: "destructive" }); }
  };
  const editVenue = (v) => setVenueModal({ ...v, capacity: v.capacity || "" });

  const saveVenueModal = async (e) => {
    e.preventDefault();
    const m = venueModal;
    if (!m.name?.trim()) { toast({ title: "Name required", variant: "destructive" }); return; }
    if (m.capacity !== "" && (isNaN(Number(m.capacity)) || Number(m.capacity) < 0)) {
      toast({ title: "Capacity must be a positive number", variant: "destructive" }); return;
    }
    setVenueBusy(true);
    try {
      await api.put(`/admin/venues/${m.id}`, {
        name: m.name.trim(), city: (m.city || "").trim(), address: (m.address || "").trim(),
        capacity: parseInt(m.capacity) || 0, slug: (m.slug || "").trim(), image_url: m.image_url || "",
      });
      loadVenues();
      setVenueModal(null);
      toast({ title: "Venue updated" });
    } catch (e2) {
      toast({ title: "Error", description: e2?.response?.data?.detail || "Could not update", variant: "destructive" });
    } finally { setVenueBusy(false); }
  };

  const editCitySlug = (c) => {
    const slug = window.prompt(`Public link slug for "${c.name}" (only super-admins can change this):`, c.slug || "");
    if (slug === null) return;
    saveCities(cities.map((x) => (x.name === c.name ? { ...x, slug: slug.trim() } : x)));
  };

  const loadActs = () => api.get("/admin/acts").then(({ data }) => setActs(Array.isArray(data) ? data : [])).catch(() => {});
  const editAct = async (a) => {
    const name = window.prompt(`Artist name:`, a.name || "");
    if (name === null) return;
    const slug = window.prompt(`Public link slug for "${a.name}":`, a.slug || "");
    if (slug === null) return;
    try {
      await api.put(`/admin/acts/${a.id}`, { name: name.trim(), slug: slug.trim(), bio: a.bio || "", image_url: a.image_url || "" });
      loadActs();
      toast({ title: "Artist updated" });
    } catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Could not update", variant: "destructive" }); }
  };

  const setVenueLogin = async (v) => {
    const email = window.prompt(`Login email for "${v.name}" door staff:`);
    if (!email) return;
    const password = window.prompt(`Set a password for ${email}:`);
    if (!password) return;
    try {
      const { data } = await api.post(`/admin/venues/${v.id}/login`, { email: email.trim(), password, origin: window.location.origin });
      toast({ title: "Venue login saved", description: data.emailed ? `Sign-in details emailed to ${email.trim()}.` : `${v.name} can now sign in with ${email.trim()}.` });
    } catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Could not set login", variant: "destructive" }); }
  };
  const resolveFlag = async (fid) => {
    try { await api.post(`/admin/event-flags/${fid}/resolve`); setFlags((f) => f.map((x) => x.id === fid ? { ...x, status: "resolved" } : x)); }
    catch { toast({ title: "Error", variant: "destructive" }); }
  };
  const saveContent = async () => {
    await api.put("/site-content", { content });
    toast({ title: "Site content updated", description: "Marketing page reflects your edits." });
  };
  const loginAs = async (bid) => {
    try {
      const { data } = await api.post(`/admin/impersonate/${bid}`);
      localStorage.setItem("gp_token", data.token);
      window.location.href = "/dashboard";
    } catch (err) {
      toast({ title: "Error", description: "Could not log in as business", variant: "destructive" });
    }
  };
  const reloadBiz = () => {
    api.get("/admin/businesses").then(({ data }) => setBusinesses(data));
    api.get("/admin/fees").then(({ data }) => setFees(data)).catch(() => {});
  };
  const toggleFeatured = async (b) => {
    try {
      const { data } = await api.post(`/admin/businesses/${b.id}/featured`, { featured: !b.featured_home });
      toast({ title: data.featured_home ? "Featured on homepage" : "Removed from homepage" });
      reloadBiz();
    } catch { toast({ title: "Error", description: "Could not update", variant: "destructive" }); }
  };
  const togglePwa = async (b) => {
    try {
      const { data } = await api.post(`/admin/businesses/${b.id}/pwa`, { enabled: !b.pwa_enabled });
      toast({ title: data.pwa_enabled ? "Installable app enabled" : "Installable app disabled" });
      reloadBiz();
    } catch { toast({ title: "Error", description: "Could not update", variant: "destructive" }); }
  };
  const archiveBiz = async (b) => {
    try {
      const { data } = await api.post(`/admin/businesses/${b.id}/archive`);
      toast({ title: data.archived ? "Business archived" : "Business restored" });
      reloadBiz();
    } catch { toast({ title: "Error", description: "Could not archive", variant: "destructive" }); }
  };
  const deleteBiz = async (b) => {
    if (!window.confirm(`Permanently delete "${b.name}" and all its vouchers? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/businesses/${b.id}`);
      toast({ title: "Business deleted" });
      reloadBiz();
    } catch { toast({ title: "Error", description: "Could not delete", variant: "destructive" }); }
  };
  const setApproval = async (b, approved, fee_status) => {
    try {
      await api.post(`/admin/businesses/${b.id}/approval`, { approved, fee_status: fee_status || (b.fee_status && b.fee_status !== "standard" ? b.fee_status : "waived") });
      toast({ title: approved ? "Business approved" : "Approval removed" });
      reloadBiz();
    } catch { toast({ title: "Error", description: "Could not update approval", variant: "destructive" }); }
  };
  const openEventFees = async (b) => {
    try {
      const { data } = await api.get(`/admin/businesses/${b.id}/events`);
      setEventFeeList(data); setEventFeeBiz(b);
    } catch { toast({ title: "Error", description: "Could not load events", variant: "destructive" }); }
  };
  const setEventPin = async (ev) => {
    try {
      const { data } = await api.post(`/admin/products/${ev.id}/pin`, { featured: !ev.homepage_pinned });
      toast({ title: data.homepage_pinned ? "Pinned to top" : "Unpinned" });
      openEventFees(eventFeeBiz);
    } catch { toast({ title: "Error", description: "Could not pin event", variant: "destructive" }); }
  };
  const setEventOverride = async (ev) => {
    const pctStr = window.prompt(`Commission % for event "${ev.title}" (blank = use business/platform default):`, ev.commission_pct_override != null ? String(ev.commission_pct_override) : "");
    if (pctStr === null) return;
    const fixedStr = window.prompt(`Fixed fee £ per ticket for "${ev.title}" (blank = default):`, ev.commission_fixed_override != null ? String(ev.commission_fixed_override) : "");
    if (fixedStr === null) return;
    const commission_pct = pctStr.trim() === "" ? null : parseFloat(pctStr);
    const commission_fixed = fixedStr.trim() === "" ? null : parseFloat(fixedStr);
    try {
      await api.post(`/admin/products/${ev.id}/fee-override`, { commission_pct, commission_fixed });
      toast({ title: "Event fee saved", description: (commission_pct == null && commission_fixed == null) ? "Reverted to default." : `Now ${commission_pct ?? "default"}%${commission_fixed ? ` + £${commission_fixed}/ticket` : ""}.` });
      openEventFees(eventFeeBiz);
    } catch { toast({ title: "Error", description: "Could not save event fee", variant: "destructive" }); }
  };
  const setFeeOverride = async (b) => {
    const pctStr = window.prompt(`Commission % for "${b.name}" (blank = platform default):`, b.commission_pct_override != null ? String(b.commission_pct_override) : "");
    if (pctStr === null) return;
    const fixedStr = window.prompt(`Fixed fee £ per ticket for "${b.name}" (blank = platform default):`, b.commission_fixed_override != null ? String(b.commission_fixed_override) : "");
    if (fixedStr === null) return;
    const commission_pct = pctStr.trim() === "" ? null : parseFloat(pctStr);
    const commission_fixed = fixedStr.trim() === "" ? null : parseFloat(fixedStr);
    try {
      await api.post(`/admin/businesses/${b.id}/fee-override`, { commission_pct, commission_fixed });
      toast({ title: "Fee override saved", description: (commission_pct == null && commission_fixed == null) ? "Reverted to platform default." : `Now ${commission_pct ?? fees.commission_pct}%${commission_fixed ? ` + £${commission_fixed}/ticket` : ""}.` });
      api.get("/admin/fees").then(({ data }) => setFees(data)).catch(() => {});
    } catch { toast({ title: "Error", description: "Could not save fee override", variant: "destructive" }); }
  };
  const resolvePayout = async (r, status) => {
    if (status === "paid" && !window.confirm(`Mark £${r.amount.toFixed(2)} to ${r.business_name} as PAID? Only do this after you've sent the money.`)) return;
    try {
      await api.post(`/admin/payout-requests/${r.id}/resolve`, { status });
      toast({ title: status === "paid" ? "Marked paid" : "Request rejected" });
      loadPayouts();
    } catch { toast({ title: "Error", description: "Could not update payout", variant: "destructive" }); }
  };
  const cancelByCode = async () => {
    const code = window.prompt("Enter the ticket code to cancel:");
    if (!code) return;
    try {
      const { data } = await api.post(`/admin/tickets/${code.trim()}/cancel`);
      toast({ title: "Ticket cancelled", description: data.refunded ? "Refunded and seat released." : "Seat released." });
    } catch (err) { toast({ title: "Error", description: err?.response?.data?.detail || "Could not cancel", variant: "destructive" }); }
  };
  const issueInvoice = async (b) => {
    const amt = window.prompt(`Invoice amount (£) for ${b.name}:`, "19.99");
    if (amt === null) return;
    const period = window.prompt("Period (optional, e.g. June 2026):", "") || "";
    try {
      await api.post("/admin/invoices", { business_id: b.id, amount: parseFloat(amt) || 0, period });
      toast({ title: "Invoice issued" });
      loadInvoices();
    } catch { toast({ title: "Error", description: "Could not issue invoice", variant: "destructive" }); }
  };
  const markInvoicePaid = async (inv) => {
    try { await api.post(`/admin/invoices/${inv.id}/paid`); toast({ title: "Marked paid" }); loadInvoices(); }
    catch { toast({ title: "Error", description: "Could not update", variant: "destructive" }); }
  };
  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.number}? This can't be undone.`)) return;
    try { await api.delete(`/admin/invoices/${inv.id}`); toast({ title: "Invoice deleted" }); loadInvoices(); }
    catch { toast({ title: "Error", description: "Could not delete", variant: "destructive" }); }
  };
  const runMonthlyInvoices = async () => {
    try {
      const { data } = await api.post("/admin/run-monthly-invoices");
      const own = data.own_stripe_fees || {};
      toast({ title: "Monthly invoicing run", description: `${data.created} subscription + ${own.created || 0} own-Stripe fee invoice(s) for ${data.period}.` });
      loadInvoices();
      loadOwnFees();
    } catch { toast({ title: "Error", description: "Could not run invoicing", variant: "destructive" }); }
  };
  const runOverdueChase = async () => {
    try {
      const { data } = await api.post("/admin/run-overdue-chase");
      toast({ title: "Overdue chase run", description: `${data.chased} business(es) chased — ${data.emailed} email(s), ${data.texted} text(s). ${data.flagged} newly flagged.` });
      loadInvoices();
    } catch { toast({ title: "Error", description: "Could not chase overdue invoices", variant: "destructive" }); }
  };
  const openInvoicePdf = async (id) => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast({ title: "Error", description: "Could not open invoice", variant: "destructive" }); }
  };
  const runReminders = async () => {
    try {
      const { data } = await api.post("/admin/run-expiry-reminders");
      toast({ title: "Expiry reminders run", description: `Sent ${data.sent}, skipped ${data.skipped} (checked ${data.checked}).` });
    } catch (err) {
      toast({ title: "Error", description: "Could not run reminders", variant: "destructive" });
    }
  };
  const runScheduledSends = async () => {
    try {
      const { data } = await api.post("/admin/run-scheduled-sends");
      toast({ title: "Scheduled sends run", description: `Delivered ${data.sent} pending order(s).` });
    } catch (err) {
      toast({ title: "Error", description: "Could not run scheduled sends", variant: "destructive" });
    }
  };
  const runCartNudges = async () => {
    try {
      const { data } = await api.post("/admin/run-cart-nudges?min_age_minutes=0");
      toast({ title: "Cart nudges run", description: data.reason ? data.reason : `Sent ${data.sent}, skipped ${data.skipped} (of ${data.candidates} pending).` });
    } catch (err) {
      toast({ title: "Error", description: "Could not run cart nudges", variant: "destructive" });
    }
  };
  const runActivationNudges = async () => {
    try {
      const { data } = await api.post("/admin/run-activation-nudges?min_age_days=0");
      toast({ title: "Activation nudges run", description: data.reason ? data.reason : `Sent ${data.sent}, skipped ${data.skipped} (checked ${data.checked} organisers).` });
    } catch (err) {
      toast({ title: "Error", description: "Could not run activation nudges", variant: "destructive" });
    }
  };

  const tabs = [
    { id: "businesses", label: "Businesses", icon: Building2 },
    { id: "revenue", label: "Revenue", icon: PoundSterling },
    { id: "orders", label: "All orders", icon: Receipt },
    { id: "tickets", label: "All tickets", icon: Ticket },
    ...(isFullAdmin ? [
      { id: "platform", label: "Platform & Stripe", icon: CreditCard },
      { id: "cities", label: "Cities", icon: MapPin },
      { id: "venues", label: "Venues", icon: Building2 },
      { id: "acts", label: "Artists", icon: Mic2 },
      { id: "content", label: "Site content", icon: FileText },
      { id: "emails", label: "Emails", icon: Mail },
    ] : []),
    { id: "articles", label: "Articles", icon: Newspaper },
    ...(isFullAdmin ? [{ id: "staff", label: "Staff", icon: Users }] : []),
  ];

  const totalSales = businesses ? businesses.reduce((s, b) => s + (b.total_sales || 0), 0) : 0;
  const totalVouchers = businesses ? businesses.reduce((s, b) => s + (b.voucher_count || 0), 0) : 0;

  return (
    <div className="min-h-screen bg-[#f6f8fb]">
      {venueModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setVenueModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="venue-edit-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#16243b] text-lg flex items-center gap-2"><Building2 size={18} className="text-[#2ec9c9]" /> Edit venue</h3>
              <button onClick={() => setVenueModal(null)} className="text-[#8a97a8] hover:text-[#16243b]"><X size={20} /></button>
            </div>
            <form onSubmit={saveVenueModal} className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Venue name</label>
                <input value={venueModal.name || ""} onChange={(e) => setVenueModal({ ...venueModal, name: e.target.value })} data-testid="vm-name"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">City</label>
                <input value={venueModal.city || ""} onChange={(e) => setVenueModal({ ...venueModal, city: e.target.value })} data-testid="vm-city"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Capacity</label>
                <input type="number" min="0" value={venueModal.capacity} onChange={(e) => setVenueModal({ ...venueModal, capacity: e.target.value })} data-testid="vm-capacity"
                  placeholder="0 = unlimited" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Address</label>
                <input value={venueModal.address || ""} onChange={(e) => setVenueModal({ ...venueModal, address: e.target.value })} data-testid="vm-address"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Public link slug</label>
                <div className="flex items-center rounded-xl border border-gray-200 focus-within:border-[#2ec9c9] overflow-hidden">
                  <span className="px-3 py-2.5 text-sm text-[#8a97a8] bg-[#f6f8fb] whitespace-nowrap">/venues/{slugify(venueModal.city || "")}/</span>
                  <input value={venueModal.slug || ""} onChange={(e) => setVenueModal({ ...venueModal, slug: e.target.value })} data-testid="vm-slug"
                    className="flex-1 px-3 py-2.5 outline-none" />
                </div>
              </div>
              <div className="sm:col-span-2">
                <ImageUpload label="Venue photo" value={venueModal.image_url} onChange={(v) => setVenueModal({ ...venueModal, image_url: v })} testid="vm-image" />
              </div>
              {(venueModal.address || venueModal.city || venueModal.name) && (
                <div className="sm:col-span-2">
                  <p className="text-sm font-medium text-[#16243b] mb-1.5">Map preview</p>
                  <div className="rounded-xl overflow-hidden border border-gray-200" data-testid="vm-map-preview">
                    <iframe title="vm-map" width="100%" height="180" loading="lazy" style={{ border: 0 }}
                      src={`https://www.google.com/maps?q=${encodeURIComponent([venueModal.name, venueModal.address, venueModal.city].filter(Boolean).join(", "))}&output=embed`} />
                  </div>
                </div>
              )}
              <div className="sm:col-span-2 flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setVenueModal(null)} className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">Cancel</button>
                <button disabled={venueBusy} data-testid="vm-save" className="gp-btn-teal rounded-xl px-6 py-2.5 text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2">
                  {venueBusy ? <Loader2 className="animate-spin" size={16} /> : null} Save venue
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <header className="bg-[#16243b] text-white">
        <div className="max-w-[1100px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-semibold">TicketsPro.app <span className="text-[#2ec9c9]">Admin</span></span>
          </div>
          <button onClick={() => { logout(); nav("/login"); }} className="flex items-center gap-2 text-sm text-[#9fb0c8] hover:text-white"><LogOut size={16} /> Sign out</button>
        </div>
      </header>

      <div className={`${tab === "orders" || tab === "tickets" ? "max-w-[1400px]" : "max-w-[1100px]"} mx-auto px-4 sm:px-6 py-6 sm:py-8`}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Stat label="Businesses" value={businesses?.length ?? "–"} icon={Building2} />
          <Stat label="Total ticket sales" value={`£${totalSales.toFixed(2)}`} icon={PoundSterling} />
          <Stat label="Tickets issued" value={totalVouchers} icon={Ticket} />
        </div>

        <div className="flex flex-wrap gap-2 mb-6 -mx-1 px-1 overflow-x-auto sm:overflow-visible">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-colors ${tab === t.id ? "bg-[#16243b] text-white" : "bg-white text-[#5a6b80]"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "businesses" && (
          <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-x-auto">
            {!businesses ? <Loader2 className="animate-spin text-[#2ec9c9] m-8" /> : (
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Business", "Store URL", "Approval", "Homepage", "App", "Tickets", "Sales", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {businesses.map((b) => (
                  <tr key={b.id} className={`border-t border-gray-50 ${b.archived ? "opacity-50" : ""}`}>
                    <td className="px-5 py-3 font-medium text-[#16243b]">{b.name} {b.archived && <span className="text-xs text-amber-600 ml-1">(archived)</span>}</td>
                    <td className="px-5 py-3"><a href={`/store/${b.slug}`} target="_blank" rel="noreferrer" className="text-[#1fb0b0] hover:underline">/store/{b.slug}</a></td>
                    <td className="px-5 py-3">
                      {b.admin_approved ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Approved</span>
                          <select value={b.fee_status || "waived"} onChange={(e) => setApproval(b, true, e.target.value)} data-testid={`fee-status-${b.id}`}
                            className="text-xs rounded-lg border border-gray-200 px-1.5 py-1 bg-white">
                            <option value="waived">Fee waived</option>
                            <option value="invoiced">Invoiced</option>
                          </select>
                          {b.fee_status === "invoiced" && <button onClick={() => issueInvoice(b)} data-testid={`issue-invoice-${b.id}`} className="text-xs text-[#1fb0b0] hover:underline">Issue invoice</button>}
                          <button onClick={() => setApproval(b, false)} className="text-xs text-red-400 hover:underline" data-testid={`unapprove-${b.id}`}>Remove</button>
                        </div>
                      ) : (
                        <button onClick={() => setApproval(b, true, "waived")} data-testid={`approve-${b.id}`} className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium">Approve (no fee)</button>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <button onClick={() => toggleFeatured(b)} data-testid={`featured-${b.id}`}
                        className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${b.featured_home ? "bg-[#2ec9c9] justify-end" : "bg-gray-300 justify-start"}`}
                        title={b.featured_home ? "Shown on homepage — click to hide" : "Hidden from homepage — click to feature"}>
                        <span className="w-5 h-5 bg-white rounded-full shadow" />
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <button onClick={() => togglePwa(b)} data-testid={`pwa-${b.id}`}
                        className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${b.pwa_enabled ? "bg-[#2ec9c9] justify-end" : "bg-gray-300 justify-start"}`}
                        title={b.pwa_enabled ? "Installable app enabled — click to disable" : "Enable installable app (PWA) for this business"}>
                        <span className="w-5 h-5 bg-white rounded-full shadow" />
                      </button>
                    </td>
                    <td className="px-5 py-3 text-[#5a6b80]">{b.voucher_count}</td>
                    <td className="px-5 py-3 text-[#16243b]">£{(b.total_sales || 0).toFixed(2)}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button onClick={() => loginAs(b.id)} data-testid={`login-as-${b.id}`} className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1"><LogIn size={13} /> Log in as</button>
                      <button onClick={() => archiveBiz(b)} data-testid={`archive-${b.id}`} className="ml-2 text-xs font-medium text-amber-600 hover:underline">{b.archived ? "Restore" : "Archive"}</button>
                      <button onClick={() => deleteBiz(b)} data-testid={`delete-${b.id}`} className="ml-2 text-red-400 hover:text-red-600"><Trash2 size={14} className="inline" /></button>
                    </td>
                  </tr>
                ))}
                {businesses.length === 0 && <tr><td colSpan={8} className="px-5 py-8 text-center text-[#8a97a8]">No businesses yet.</td></tr>}
              </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "businesses" && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#16243b]">Payout requests</h3>
              <button onClick={cancelByCode} data-testid="admin-cancel-ticket-btn" className="text-xs font-medium text-red-500 hover:underline">Cancel a ticket by code</button>
            </div>
            <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-x-auto" data-testid="payout-requests">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Business", "Amount", "Requested", "Status", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {payoutReqs.map((r) => (
                    <tr key={r.id} className="border-t border-gray-50" data-testid={`payout-req-${r.id}`}>
                      <td className="px-5 py-3 text-[#16243b]">{r.business_name}</td>
                      <td className="px-5 py-3 font-semibold text-[#16243b]">£{r.amount.toFixed(2)}</td>
                      <td className="px-5 py-3 text-[#8a97a8]">{new Date(r.created_at).toLocaleDateString("en-GB")}</td>
                      <td className="px-5 py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${r.status === "paid" ? "bg-green-100 text-green-700" : r.status === "rejected" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{r.status}</span></td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        {r.status === "requested" && (
                          <>
                            <button onClick={() => resolvePayout(r, "paid")} data-testid={`payout-paid-${r.id}`} className="gp-btn-teal rounded-full px-3 py-1.5 text-xs font-medium">Mark paid</button>
                            <button onClick={() => resolvePayout(r, "rejected")} className="ml-2 text-xs text-red-400 hover:underline">Reject</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {payoutReqs.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-[#8a97a8]">No payout requests.</td></tr>}
                </tbody>
              </table>
            </div>

            {flags.filter((f) => f.status === "open").length > 0 && (
              <div className="mt-6" data-testid="event-flags">
                <h3 className="font-semibold text-[#16243b] mb-1 flex items-center gap-2 text-red-600">⚠ Venue-flagged events</h3>
                <p className="text-sm text-[#8a97a8] mb-3">A venue reported these events as possibly fraudulent. Review the organiser.</p>
                <div className="space-y-2">
                  {flags.filter((f) => f.status === "open").map((f) => (
                    <div key={f.id} data-testid={`flag-${f.id}`} className="flex items-center justify-between bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm">
                      <span className="text-[#16243b]"><strong>{f.product_title}</strong> · {f.business_name} <span className="text-[#8a97a8]">— flagged by {f.venue_name}</span></span>
                      <button onClick={() => resolveFlag(f.id)} data-testid={`resolve-flag-${f.id}`} className="text-xs text-[#2ec9c9] hover:underline font-medium">Mark reviewed</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {ownFees && ownFees.businesses.length > 0 && (
              <div className="mt-6" data-testid="own-stripe-fees-report">
                <h3 className="font-semibold text-[#16243b] mb-1">Own-Stripe fees to invoice</h3>
                <p className="text-sm text-[#8a97a8] mb-3">Platform commission accrued on sales taken via organisers' own Stripe keys — not yet invoiced. Billed when you run monthly invoicing.</p>
                <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Organiser", "Sales", "Fees owed"].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
                    <tbody>
                      {ownFees.businesses.map((r) => (
                        <tr key={r.business_id} className="border-t border-gray-50" data-testid={`own-fee-row-${r.business_id}`}>
                          <td className="px-5 py-3 text-[#16243b] font-medium">{r.business_name}</td>
                          <td className="px-5 py-3 text-[#5a6b80]">{r.sales_count}</td>
                          <td className="px-5 py-3 font-semibold text-[#16243b]">£{r.fees_owed.toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-gray-100 bg-[#f6f8fb]">
                        <td className="px-5 py-3 font-semibold text-[#16243b]">Total to invoice ({ownFees.period})</td>
                        <td className="px-5 py-3"></td>
                        <td className="px-5 py-3 font-bold text-[#16243b]" data-testid="own-fees-total">£{ownFees.total_owed.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <h3 className="font-semibold text-[#16243b] mt-6 mb-3 flex items-center justify-between">Invoices
              <div className="flex items-center gap-2">
                <button onClick={runOverdueChase} data-testid="run-overdue-chase-btn" className="gp-btn-outline rounded-full px-4 py-1.5 text-xs font-medium">Chase overdue now</button>
                <button onClick={runMonthlyInvoices} data-testid="run-monthly-invoices-btn" className="gp-btn-outline rounded-full px-4 py-1.5 text-xs font-medium">Run monthly invoicing now</button>
              </div>
            </h3>
            <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-x-auto" data-testid="invoices-panel">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Number", "Business", "Amount", "Issued", "Status", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-t border-gray-50" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-5 py-3 font-mono text-[#16243b]">{inv.number}</td>
                      <td className="px-5 py-3 text-[#5a6b80]">{inv.business_name}</td>
                      <td className="px-5 py-3 font-semibold text-[#16243b]">£{inv.amount.toFixed(2)}</td>
                      <td className="px-5 py-3 text-[#8a97a8]">{new Date(inv.issued_at).toLocaleDateString("en-GB")}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${inv.status === "paid" ? "bg-green-100 text-green-700" : inv.overdue ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`} data-testid={`invoice-status-${inv.id}`}>
                          {inv.status === "paid" ? "paid" : inv.overdue ? "overdue" : inv.status}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <button onClick={() => openInvoicePdf(inv.id)} className="text-[#1fb0b0] hover:underline text-xs" data-testid={`admin-invoice-pdf-${inv.id}`}>PDF</button>
                        {inv.status !== "paid" && <button onClick={() => markInvoicePaid(inv)} data-testid={`invoice-paid-${inv.id}`} className="ml-3 gp-btn-teal rounded-full px-3 py-1.5 text-xs font-medium">Mark paid</button>}
                        <button onClick={() => deleteInvoice(inv)} data-testid={`invoice-delete-${inv.id}`} className="ml-3 text-red-500 hover:text-red-600 text-xs">Delete</button>
                      </td>
                    </tr>
                  ))}
                  {invoices.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-[#8a97a8]">No invoices yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "revenue" && (
          <div data-testid="revenue-tab">
            {!fees ? <Loader2 className="animate-spin text-[#2ec9c9] m-8" /> : (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <Stat label={`Total commission (${fees.commission_pct}%${fees.commission_fixed ? ` + £${fees.commission_fixed}/ticket` : ""})`} value={`£${fees.total_commission.toFixed(2)}`} icon={PoundSterling} />
                  <Stat label="Gross ticket sales" value={`£${fees.total_gross_sales.toFixed(2)}`} icon={Ticket} />
                  <Stat label="Active subscriptions" value={fees.active_subscriptions} icon={CheckCircle2} />
                  <Stat label={`Subscription MRR (£${fees.monthly_fee}/mo)`} value={`£${fees.estimated_mrr.toFixed(2)}`} icon={CreditCard} />
                </div>
                <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-x-auto">
                  <div className="p-4 border-b border-gray-50">
                    <input value={revFilter} onChange={(e) => setRevFilter(e.target.value)} placeholder="Filter businesses by name…" data-testid="revenue-filter"
                      className="w-full max-w-sm rounded-xl border border-gray-200 px-4 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                  </div>
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Business", "Gross sales", `Commission (${fees.commission_pct}%)`, "Fee override", "Stripe", "Subscription", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
                    <tbody>
                      {fees.businesses.filter((b) => b.name.toLowerCase().includes(revFilter.toLowerCase())).map((b) => (
                        <tr key={b.id} className="border-t border-gray-50" data-testid={`fee-row-${b.id}`}>
                          <td className="px-5 py-3 font-medium text-[#16243b]">{b.name}</td>
                          <td className="px-5 py-3 text-[#5a6b80]">£{b.gross.toFixed(2)}</td>
                          <td className="px-5 py-3 text-[#16243b] font-medium">£{b.commission.toFixed(2)}</td>
                          <td className="px-5 py-3">
                            {(b.commission_pct_override != null || b.commission_fixed_override != null)
                              ? <span className="text-xs font-semibold text-[#16243b] bg-[#eefafa] px-2 py-1 rounded-full" title={b.fee_override_changed_by ? `Set by ${b.fee_override_changed_by}${b.fee_override_changed_at ? ` on ${new Date(b.fee_override_changed_at).toLocaleDateString("en-GB")}` : ""}` : ""}>{b.commission_pct_override != null ? `${b.commission_pct_override}%` : `${fees.commission_pct}%`}{b.commission_fixed_override ? ` + £${b.commission_fixed_override}` : ""}</span>
                              : <span className="text-xs text-[#8a97a8]">Platform default</span>}
                            <button onClick={() => setFeeOverride(b)} data-testid={`fee-override-${b.id}`} className="ml-2 text-xs text-[#1fb0b0] hover:underline">Edit</button>
                            <button onClick={() => openEventFees(b)} data-testid={`event-fees-${b.id}`} className="ml-2 text-xs text-[#1fb0b0] hover:underline">Events</button>
                            {b.fee_override_changed_by && (b.commission_pct_override != null || b.commission_fixed_override != null) && (
                              <p className="text-[10px] text-[#8a97a8] mt-1">by {b.fee_override_changed_by}{b.fee_override_changed_at ? ` · ${new Date(b.fee_override_changed_at).toLocaleDateString("en-GB")}` : ""}</p>
                            )}
                          </td>
                          <td className="px-5 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${b.stripe_connected ? "bg-green-100 text-green-700" : "bg-slate-100 text-[#8a97a8]"}`}>{b.stripe_connected ? "Connected" : "Not connected"}</span></td>
                          <td className="px-5 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${b.subscription_status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-[#8a97a8]"}`}>{b.subscription_status}</span></td>
                          <td className="px-5 py-3 text-right"><button onClick={() => deleteBiz(b)} data-testid={`revenue-delete-${b.id}`} className="text-red-400 hover:text-red-600"><Trash2 size={14} className="inline" /></button></td>
                        </tr>
                      ))}
                      {fees.businesses.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-[#8a97a8]">No data yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "platform" && platform && (
          <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] max-w-[560px] space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <AdminField label="Monthly fee (£)" type="number" value={platform.monthly_fee} onChange={(v) => setPlatform({ ...platform, monthly_fee: parseFloat(v) || 0 })} />
              <AdminField label="Commission (%)" type="number" value={platform.commission_pct} onChange={(v) => setPlatform({ ...platform, commission_pct: parseFloat(v) || 0 })} />
              <AdminField label="Fixed fee / ticket (£)" type="number" value={platform.commission_fixed} onChange={(v) => setPlatform({ ...platform, commission_fixed: parseFloat(v) || 0 })} />
            </div>
            <p className="text-xs text-[#8a97a8] -mt-1">Platform fee per order = commission % of the ticket subtotal + the fixed fee × number of tickets. E.g. 5% + £1/ticket.</p>

            <div className="mt-6 pt-6 border-t border-gray-100" data-testid="category-manager">
              <h4 className="font-semibold text-[#16243b] mb-1">Event categories</h4>
              <p className="text-xs text-[#8a97a8] mb-3">Categories organisers can tag events with, and that appear on the homepage & browse pages.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {cats.map((c) => (
                  <span key={c} data-testid={`admin-cat-${c}`} className="inline-flex items-center gap-1.5 text-sm bg-[#eefafa] text-[#16243b] rounded-full pl-3 pr-2 py-1.5">
                    {c}
                    <button onClick={() => removeCat(c)} data-testid={`remove-cat-${c}`} className="text-[#8a97a8] hover:text-red-500"><X size={14} /></button>
                  </span>
                ))}
                {cats.length === 0 && <span className="text-sm text-[#8a97a8]">No categories yet.</span>}
              </div>
              <div className="flex gap-2 max-w-sm">
                <input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} data-testid="new-category-input"
                  placeholder="Add a category…" className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                <button onClick={addCat} data-testid="add-category-btn" className="gp-btn-teal rounded-lg px-4 py-2 text-sm font-medium">Add</button>
              </div>
            </div>

            <div className="rounded-2xl border border-[#2ec9c9]/30 bg-[#eefafa] p-5" data-testid="stripe-managed-panel">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-[#2ec9c9]/20 text-[#0f8a8a] flex items-center justify-center shrink-0"><CreditCard size={18} /></span>
                <div className="text-sm text-[#16243b]">
                  <h4 className="font-semibold">Payments are managed by Emergent</h4>
                  <p className="mt-1 text-[#3a4a5e]">Card payments run through the Stripe account connected to this app — you don't paste any keys here. Approved organisers without their own Stripe collect into this platform account automatically.</p>
                  <p className="mt-3 font-semibold">To go live with real payments:</p>
                  <ol className="mt-1 list-decimal list-inside space-y-1 text-[#3a4a5e]">
                    <li>Open the <strong>Payments</strong> tab in the Emergent dashboard and complete Stripe activation (KYC).</li>
                    <li>Once it shows <strong>Live / Activated</strong>, <strong>Redeploy</strong> the app — this swaps the test key for your live key.</li>
                    <li>Test a purchase on your live site. Still seeing a test key? Email <strong>support@emergent.sh</strong> with your job ID.</li>
                  </ol>
                  <p className="mt-3 text-xs text-[#8a97a8]">Until activation is complete the app uses Stripe test mode (card <span className="font-mono">4242 4242 4242 4242</span>).</p>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-[#16243b]">Email delivery (SMTP)</h4>
                  <p className="text-sm text-[#8a97a8]">Email ticket PDFs to buyers on purchase.</p>
                </div>
                <label className="flex items-center gap-2 text-sm text-[#16243b]">
                  <input type="checkbox" checked={!!platform.email_enabled} onChange={(e) => setPlatform({ ...platform, email_enabled: e.target.checked })} /> Enabled
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <AdminField label="SMTP host" value={platform.smtp_host} onChange={(v) => setPlatform({ ...platform, smtp_host: v })} placeholder="smtp.example.com" />
                <AdminField label="SMTP port" type="number" value={platform.smtp_port} onChange={(v) => setPlatform({ ...platform, smtp_port: parseInt(v) || 587 })} placeholder="587" />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <AdminField label="SMTP username" value={platform.smtp_user} onChange={(v) => setPlatform({ ...platform, smtp_user: v })} />
                <AdminField label="SMTP password" type="password" value={platform.smtp_password} onChange={(v) => setPlatform({ ...platform, smtp_password: v })} />
              </div>
              <div className="mt-4">
                <AdminField label="From address" value={platform.smtp_from} onChange={(v) => setPlatform({ ...platform, smtp_from: v })} placeholder="vouchers@yourbrand.com" />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#16243b] mt-4">
                <input type="checkbox" checked={platform.smtp_use_tls !== false} onChange={(e) => setPlatform({ ...platform, smtp_use_tls: e.target.checked })} /> Use STARTTLS (recommended for port 587)
              </label>
            </div>

            {/* SMS / Twilio */}
            <div className="border-t border-gray-100 mt-6 pt-6" data-testid="sms-settings">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-[#16243b]">SMS reminders (Twilio)</h4>
                <label className="flex items-center gap-2 text-sm text-[#16243b]">
                  <input type="checkbox" data-testid="sms-enabled-toggle" checked={!!platform.sms_enabled} onChange={(e) => setPlatform({ ...platform, sms_enabled: e.target.checked })} /> Enabled system-wide
                </label>
              </div>
              <p className="text-xs text-[#8a97a8] mb-3">When enabled, every venue automatically texts ticket-holders their QR link ~2 days before the event, using the mobile captured at checkout.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <AdminField label="Twilio Account SID" value={platform.twilio_account_sid} onChange={(v) => setPlatform({ ...platform, twilio_account_sid: v })} placeholder="AC…" mono />
                <AdminField label="Twilio Auth Token" type="password" value={platform.twilio_auth_token} onChange={(v) => setPlatform({ ...platform, twilio_auth_token: v })} mono />
              </div>
              <AdminField label="Twilio From number / Messaging Service SID" value={platform.twilio_from} onChange={(v) => setPlatform({ ...platform, twilio_from: v })} placeholder="+441234567890 or MG…" mono />
              <div className="mt-4 bg-[#f6f8fb] rounded-xl p-4" data-testid="test-sms-box">
                <p className="text-sm font-medium text-[#16243b] mb-1">Send a test SMS</p>
                <p className="text-xs text-[#8a97a8] mb-3">Confirm your Twilio credentials work before going live. Save your settings first, then send a test to your own mobile.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input value={testSms} onChange={(e) => setTestSms(e.target.value)} data-testid="test-sms-number"
                    placeholder="+447700900123" className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
                  <button type="button" onClick={sendTestSms} disabled={testSmsSending} data-testid="send-test-sms-btn"
                    className="gp-btn-teal rounded-xl px-5 py-2.5 text-sm font-medium disabled:opacity-60 whitespace-nowrap">
                    {testSmsSending ? "Sending…" : "Send test SMS"}
                  </button>
                </div>
              </div>
              <button type="button" onClick={runReminders} data-testid="run-reminders-btn" className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium mt-4">
                Run event reminders now (email + SMS)
              </button>
              <p className="text-xs text-[#8a97a8] mt-2">Sends email &amp; (if SMS enabled) text reminders ~2 days before each event. Runs automatically every 6 hours.</p>
              <div className="flex flex-wrap gap-3 mt-4">
                <button type="button" onClick={runScheduledSends} data-testid="run-scheduled-btn" className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">
                  Send scheduled vouchers now
                </button>
                <button type="button" onClick={runCartNudges} data-testid="run-cart-nudges-btn" className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">
                  Send abandoned-cart nudges now
                </button>
                <button type="button" onClick={runActivationNudges} data-testid="run-activation-nudges-btn" className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">
                  Send activation nudges now
                </button>
              </div>
              <p className="text-xs text-[#8a97a8] mt-2">Scheduled vouchers are delivered on their chosen date; abandoned-cart nudges email buyers who started a Stripe checkout but didn't pay. Activation nudges email organisers who, 3+ days after signup, still have no active event and haven't connected Stripe/subscribed. All run automatically every 15 minutes.</p>
            </div>

            <button onClick={savePlatform} className="gp-btn-teal rounded-xl px-6 py-3 font-medium">Save platform settings</button>
            <ChangePasswordCard />
          </div>
        )}

        {tab === "orders" && <AdminOrdersTab businesses={businesses} />}
        {tab === "tickets" && <AdminTicketsTab businesses={businesses} />}
        {tab === "emails" && <AdminEmailsTab />}

        {tab === "cities" && (
          <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] max-w-[640px]" data-testid="city-manager">
            <h4 className="font-semibold text-[#16243b] mb-1">Cities / regions</h4>
            <p className="text-xs text-[#8a97a8] mb-3">Cities organisers can tag events with, used for the location filter on the homepage & browse pages. Add latitude &amp; longitude to enable "events near me" geo-detection.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {cities.map((c) => (
                <span key={c.name} data-testid={`admin-city-${c.name}`} className="inline-flex items-center gap-1.5 text-sm bg-[#eefafa] text-[#16243b] rounded-full pl-3 pr-2 py-1.5">
                  {c.name}
                  <code className="text-[10px] text-[#8a97a8]">/{c.slug || "—"}</code>
                  {(typeof c.lat === "number" && typeof c.lng === "number")
                    ? <span className="text-[10px] text-[#2ec9c9]">📍</span>
                    : <span className="text-[10px] text-amber-500" title="No coordinates — won't be used for geo-detect">⚠</span>}
                  <button onClick={() => editCitySlug(c)} data-testid={`edit-city-slug-${c.name}`} title="Edit link slug" className="text-[#8a97a8] hover:text-[#2ec9c9]"><Pencil size={12} /></button>
                  <button onClick={() => removeCity(c.name)} data-testid={`remove-city-${c.name}`} className="text-[#8a97a8] hover:text-red-500"><X size={14} /></button>
                </span>
              ))}
              {cities.length === 0 && <span className="text-sm text-[#8a97a8]">No cities yet.</span>}
            </div>
            <div className="flex flex-wrap gap-2 items-center max-w-xl">
              <input value={newCity.name} onChange={(e) => setNewCity({ ...newCity, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addCity()} data-testid="new-city-name"
                placeholder="City name" className="flex-1 min-w-[140px] rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
              <input type="number" step="any" value={newCity.lat} onChange={(e) => setNewCity({ ...newCity, lat: e.target.value })} data-testid="new-city-lat"
                placeholder="Lat" className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
              <input type="number" step="any" value={newCity.lng} onChange={(e) => setNewCity({ ...newCity, lng: e.target.value })} data-testid="new-city-lng"
                placeholder="Lng" className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
              <button onClick={addCity} data-testid="add-city-btn" className="gp-btn-teal rounded-lg px-4 py-2 text-sm font-medium">Add</button>
            </div>
          </div>
        )}

        {tab === "venues" && (
          <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] max-w-[640px]" data-testid="venue-manager">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-[#16243b]">Venues</h4>
              <select value={venueCityFilter} onChange={(e) => setVenueCityFilter(e.target.value)} data-testid="admin-venue-filter"
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-[#2ec9c9] bg-white">
                <option value="all">All cities</option>
                {cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <p className="text-xs text-[#8a97a8] mb-3">Shared venues organisers pick from. Remove duplicates or bad entries here.</p>
            <div className="space-y-1.5 mb-3 max-h-96 overflow-y-auto">
              {venues.filter((v) => venueCityFilter === "all" || v.city === venueCityFilter).map((v) => (
                <div key={v.id} data-testid={`admin-venue-${v.id}`} className="flex items-center justify-between text-sm bg-[#f6f8fb] rounded-lg px-3 py-2">
                  <span className="text-[#16243b]"><span className="font-medium">{v.name}</span> <span className="text-[#8a97a8]">· {v.city || "no city"}{v.capacity ? ` · cap ${v.capacity}` : ""}</span> <code className="text-[10px] text-[#8a97a8]">/venues/{v.slug || "—"}</code></span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => editVenue(v)} data-testid={`edit-venue-${v.id}`} className="text-xs text-[#5a6b80] hover:underline font-medium">Edit</button>
                    <button onClick={() => setVenueLogin(v)} data-testid={`venue-login-${v.id}`} className="text-xs text-[#2ec9c9] hover:underline font-medium">Set login</button>
                    <button onClick={() => openStaff(v)} data-testid={`venue-staff-${v.id}`} className="text-xs text-[#1fb0b0] hover:underline font-medium">Door staff</button>
                    <button onClick={() => removeVenue(v.id)} data-testid={`remove-venue-${v.id}`} className="text-[#8a97a8] hover:text-red-500"><X size={14} /></button>
                  </div>
                </div>
              ))}
              {venues.filter((v) => venueCityFilter === "all" || v.city === venueCityFilter).length === 0 && <span className="text-sm text-[#8a97a8]">No venues yet.</span>}
            </div>
            <div className="flex flex-wrap gap-2 items-center max-w-xl">
              <input value={newVenue.name} onChange={(e) => setNewVenue({ ...newVenue, name: e.target.value })} data-testid="admin-new-venue-name"
                placeholder="Venue name" className="flex-1 min-w-[140px] rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
              <select value={newVenue.city} onChange={(e) => setNewVenue({ ...newVenue, city: e.target.value })} data-testid="admin-new-venue-city"
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9] bg-white">
                <option value="">City…</option>
                {cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
              <input type="number" min="0" value={newVenue.capacity} onChange={(e) => setNewVenue({ ...newVenue, capacity: e.target.value })} data-testid="admin-new-venue-capacity"
                placeholder="Cap" className="w-20 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
              <button onClick={addVenueAdmin} data-testid="admin-add-venue-btn" className="gp-btn-teal rounded-lg px-4 py-2 text-sm font-medium">Add</button>
            </div>
          </div>
        )}

        {tab === "acts" && (
          <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] max-w-[640px]" data-testid="acts-manager">
            <h4 className="font-semibold text-[#16243b] mb-1 flex items-center gap-2"><Mic2 size={16} className="text-[#8b5cf6]" /> Artists / acts</h4>
            <p className="text-xs text-[#8a97a8] mb-3">All artists organisers have added. Each belongs to one organiser; you can edit any artist's name or public link slug.</p>
            <div className="space-y-1.5 max-h-96 overflow-y-auto">
              {acts.map((a) => (
                <div key={a.id} data-testid={`admin-act-${a.id}`} className="flex items-center justify-between text-sm bg-[#f6f8fb] rounded-lg px-3 py-2">
                  <span className="text-[#16243b] min-w-0 truncate">
                    <span className="font-medium">{a.name}</span>
                    <code className="text-[10px] text-[#8a97a8] ml-1.5">/acts/{a.slug}</code>
                    {a.business_name && <span className="text-[#8a97a8]"> · {a.business_name}</span>}
                  </span>
                  <button onClick={() => editAct(a)} data-testid={`edit-act-${a.id}`} className="text-xs text-[#5a6b80] hover:underline font-medium shrink-0 ml-3">Edit</button>
                </div>
              ))}
              {acts.length === 0 && <span className="text-sm text-[#8a97a8]">No artists yet.</span>}
            </div>
          </div>
        )}

        {tab === "content" && content && (
          <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] max-w-[760px] space-y-6">
            <div>
              <h4 className="font-semibold text-[#16243b] mb-1">Home page text &amp; images</h4>
              <p className="text-sm text-[#8a97a8] mb-4">Edit every heading, paragraph, button label and image on the public landing page. Image fields take a URL.</p>
            </div>

            <div className="border-t border-gray-100 pt-5" data-testid="homepage-sections">
              <h5 className="font-semibold text-[#16243b] text-sm mb-1">Homepage sections</h5>
              <p className="text-xs text-[#8a97a8] mb-3">Toggle whole sections on or off. Hidden sections won't appear on the public homepage until you switch them back on. Remember to Save.</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {[["marquee", "Trusted-by logos"], ["nearby", "Nearby events"], ["categories", "Category tiles"], ["whatson", "What's on / events"], ["features", "Feature showcase"], ["integrations", "Integrations"], ["clients", "Case studies"], ["pricing", "Pricing / Grow CTA"], ["blog", "Blog / articles"]].map(([key, label]) => {
                  const visible = (content.sections || {})[key] !== false;
                  return (
                    <label key={key} className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-2.5" data-testid={`section-toggle-${key}`}>
                      <span className="text-sm text-[#16243b]">{label}</span>
                      <input type="checkbox" checked={visible} data-testid={`section-checkbox-${key}`}
                        onChange={(e) => setContent({ ...content, sections: { ...(content.sections || {}), [key]: e.target.checked } })}
                        className="w-4 h-4 accent-[#2ec9c9]" />
                    </label>
                  );
                })}
              </div>
            </div>

            {CONTENT_GROUPS.map((g) => (
              <div key={g.title} className="border-t border-gray-100 pt-5">
                <h5 className="font-semibold text-[#16243b] text-sm mb-3">{g.title}</h5>
                <div className="space-y-3">
                  {g.keys.filter((k) => k in content).map((k) => {
                    const meta = FIELD_META[k] || {};
                    return (
                      <div key={k}>
                        <AdminField label={meta.label || k.replace(/_/g, " ")} value={content[k]} onChange={(v) => setContent({ ...content, [k]: v })} isImage={/image|avatar|logo|favicon/.test(k)} />
                        {meta.desc && <p className="text-xs text-[#8a97a8] mt-1">{meta.desc}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <DesignEditor content={content} setContent={setContent} />
            <ImageListEditor title="Storefront screenshots" listKey="store_slides" content={content} setContent={setContent} />
            <IntegrationLogosEditor content={content} setContent={setContent} />
            <CaseStudiesEditor content={content} setContent={setContent} />
            <HeaderLinksEditor content={content} setContent={setContent} />
            <FooterEditor content={content} setContent={setContent} />
            <SocialLinksEditor content={content} setContent={setContent} />
            <LegalEditor content={content} setContent={setContent} />

            <button onClick={saveContent} data-testid="save-content-btn" className="gp-btn-teal rounded-xl px-6 py-3 font-medium">Save content</button>
          </div>
        )}

        {tab === "articles" && (
          <ArticlesTab articles={articles} setArticles={setArticles} />
        )}

        {tab === "staff" && isFullAdmin && (
          <StaffTab currentUserId={user.id} />
        )}
      </div>

      {eventFeeBiz && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setEventFeeBiz(null)} data-testid="event-fees-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-[#16243b] mb-1">Per-event fees — {eventFeeBiz.name}</h3>
            <p className="text-sm text-[#8a97a8] mb-4">Pin an event to show it at the top of its day on the homepage, and override the commission for a single event.</p>
            <div className="space-y-2">
              {eventFeeList.length === 0 && <p className="text-sm text-[#8a97a8] py-6 text-center">This business has no events yet.</p>}
              {eventFeeList.map((ev) => (
                <div key={ev.id} className="flex items-center justify-between border border-gray-100 rounded-xl px-4 py-3" data-testid={`event-fee-row-${ev.id}`}>
                  <div>
                    <p className="font-medium text-[#16243b] text-sm flex items-center gap-1.5">{ev.homepage_pinned && <Pin size={12} className="text-[#2ec9c9]" />}{ev.title}</p>
                    <p className="text-xs text-[#8a97a8]">{(ev.commission_pct_override != null || ev.commission_fixed_override != null)
                      ? `Override: ${ev.commission_pct_override != null ? `${ev.commission_pct_override}%` : "default %"}${ev.commission_fixed_override ? ` + £${ev.commission_fixed_override}/ticket` : ""}`
                      : "Using default"}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button onClick={() => setEventPin(ev)} data-testid={`event-pin-${ev.id}`} className={`text-xs font-medium hover:underline flex items-center gap-1 ${ev.homepage_pinned ? "text-[#2ec9c9]" : "text-[#8a97a8]"}`}><Pin size={12} /> {ev.homepage_pinned ? "Pinned" : "Pin"}</button>
                    <button onClick={() => setEventOverride(ev)} data-testid={`event-fee-edit-${ev.id}`} className="text-xs text-[#1fb0b0] hover:underline font-medium">Fee</button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setEventFeeBiz(null)} className="gp-btn-outline rounded-full px-5 py-2 text-sm font-medium mt-5">Close</button>
          </div>
        </div>
      )}

      {staffVenue && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setStaffVenue(null)} data-testid="venue-staff-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-[#16243b] mb-1">Door staff — {staffVenue.name}</h3>
            <p className="text-sm text-[#8a97a8] mb-4">Create a named sign-in for each staff member so scans and door sales are attributed to whoever's on the door.</p>
            <div className="space-y-1.5 mb-4">
              {staffList.length === 0 && <p className="text-sm text-[#8a97a8] py-4 text-center">No staff logins yet.</p>}
              {staffList.map((s) => (
                <div key={s.id} data-testid={`staff-row-${s.id}`} className="flex items-center justify-between gap-3 text-sm bg-[#f6f8fb] rounded-lg px-3 py-2">
                  <span className="text-[#16243b] min-w-0 truncate"><span className="font-medium">{s.name}</span> <span className="text-[#8a97a8]">· {s.email}</span>{s.is_primary && <span className="ml-2 text-[10px] uppercase tracking-wide bg-[#2ec9c9]/15 text-[#0f8a8a] rounded px-1.5 py-0.5">Primary</span>}</span>
                  <div className="flex items-center gap-3 shrink-0">
                    <button onClick={() => resetStaff(s.id, s.email)} data-testid={`staff-reset-${s.id}`} className="text-xs text-[#2ec9c9] hover:underline font-medium">Reset password</button>
                    {staffList.length > 1 && <button onClick={() => removeStaff(s.id)} data-testid={`staff-remove-${s.id}`} className="text-[#8a97a8] hover:text-red-500"><X size={14} /></button>}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-100 pt-4 space-y-2">
              <p className="text-sm font-medium text-[#16243b]">Add door-staff login</p>
              <input value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} data-testid="staff-name"
                placeholder="Staff name (e.g. Sam on the door)" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
              <input value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} data-testid="staff-email"
                placeholder="Login email" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
              <input type="password" value={newStaff.password} onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })} data-testid="staff-password"
                placeholder="Temporary password (min 6 chars)" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
              <button onClick={addStaff} data-testid="staff-add-btn" className="gp-btn-teal rounded-lg px-4 py-2 text-sm font-medium">Add staff login</button>
            </div>
            <button onClick={() => setStaffVenue(null)} className="gp-btn-outline rounded-full px-5 py-2 text-sm font-medium mt-5">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) { toast({ title: "Too short", description: "New password must be at least 8 characters", variant: "destructive" }); return; }
    if (nw !== cf) { toast({ title: "Passwords don't match", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast({ title: "Password changed", description: "Your password has been updated." });
      setCur(""); setNw(""); setCf("");
    } catch (err) {
      toast({ title: "Could not change password", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-gray-100 pt-5 space-y-3" data-testid="change-password-card">
      <h4 className="font-semibold text-[#16243b]">Change password</h4>
      <form onSubmit={submit} className="space-y-3 max-w-[420px]">
        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required placeholder="Current password" data-testid="cp-current" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
        <input type="password" value={nw} onChange={(e) => setNw(e.target.value)} required placeholder="New password (min 8 chars)" data-testid="cp-new" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
        <input type="password" value={cf} onChange={(e) => setCf(e.target.value)} required placeholder="Confirm new password" data-testid="cp-confirm" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
        <button disabled={busy} data-testid="cp-submit" className="gp-btn-teal rounded-xl px-6 py-2.5 font-medium disabled:opacity-60">{busy ? "Saving…" : "Update password"}</button>
      </form>
    </div>
  );
}

function StaffTab({ currentUserId }) {
  const [staff, setStaff] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", staff_role: "business_manager" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/staff").then(({ data }) => setStaff(data)).catch(() => setStaff([]));
  useEffect(() => { load(); }, []);

  const roleLabel = (r) => (r === "full_admin" ? "Full Admin" : "Business Manager");

  const addStaff = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast({ title: "Password too short", description: "At least 8 characters", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/admin/staff", form);
      toast({ title: "Staff member added", description: `${form.name} can now sign in.` });
      setForm({ name: "", email: "", password: "", staff_role: "business_manager" });
      setShowAdd(false);
      load();
    } catch (err) {
      toast({ title: "Could not add staff", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const changeRole = async (s, newRole) => {
    try {
      await api.put(`/admin/staff/${s.id}`, { staff_role: newRole });
      toast({ title: "Role updated", description: `${s.name} is now ${roleLabel(newRole)}.` });
      load();
    } catch (err) {
      toast({ title: "Could not update role", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    }
  };

  const removeStaff = async (s) => {
    if (!window.confirm(`Remove ${s.name} (${s.email})? They will lose all access.`)) return;
    try {
      await api.delete(`/admin/staff/${s.id}`);
      toast({ title: "Staff member removed" });
      load();
    } catch (err) {
      toast({ title: "Could not remove", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6 max-w-[820px]" data-testid="staff-tab">
      <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <div className="flex items-center gap-2 text-sm text-[#5a6b80]">
          <ShieldCheck className="text-[#2ec9c9]" size={18} />
          <p><strong className="text-[#16243b]">Full Admins</strong> can access everything. <strong className="text-[#16243b]">Business Managers</strong> can manage businesses, revenue and articles, but not Platform &amp; Stripe or Site content.</p>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-[#16243b]">Staff members</h3>
        <button onClick={() => setShowAdd((v) => !v)} data-testid="add-staff-toggle" className="gp-btn-teal rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-1"><Plus size={15} /> Add staff</button>
      </div>

      {showAdd && (
        <form onSubmit={addStaff} className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] grid sm:grid-cols-2 gap-4" data-testid="add-staff-form">
          <AdminField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <AdminField label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="staff@yourbrand.com" />
          <AdminField label="Temporary password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} placeholder="Min 8 characters" />
          <div>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Role</label>
            <select value={form.staff_role} onChange={(e) => setForm({ ...form, staff_role: e.target.value })} data-testid="staff-role-select"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] bg-white">
              <option value="business_manager">Business Manager</option>
              <option value="full_admin">Full Admin</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <button disabled={busy} data-testid="save-staff-btn" className="gp-btn-teal rounded-xl px-6 py-2.5 font-medium disabled:opacity-60">{busy ? "Adding…" : "Add staff member"}</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-x-auto">
        {!staff ? <Loader2 className="animate-spin text-[#2ec9c9] m-8" /> : (
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Name", "Email", "Role", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} className="border-t border-gray-50" data-testid={`staff-row-${s.id}`}>
                  <td className="px-5 py-3 font-medium text-[#16243b]">{s.name} {s.id === currentUserId && <span className="text-xs text-[#8a97a8]">(you)</span>}</td>
                  <td className="px-5 py-3 text-[#5a6b80]">{s.email}</td>
                  <td className="px-5 py-3">
                    <select value={s.staff_role} disabled={s.id === currentUserId} onChange={(e) => changeRole(s, e.target.value)} data-testid={`staff-role-${s.id}`}
                      className={`rounded-lg border border-gray-200 px-2 py-1 text-xs bg-white ${s.id === currentUserId ? "opacity-60 cursor-not-allowed" : ""}`}>
                      <option value="business_manager">Business Manager</option>
                      <option value="full_admin">Full Admin</option>
                    </select>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {s.id !== currentUserId && (
                      <button onClick={() => removeStaff(s)} data-testid={`staff-delete-${s.id}`} className="text-red-400 hover:text-red-600"><Trash2 size={14} className="inline" /></button>
                    )}
                  </td>
                </tr>
              ))}
              {staff.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-[#8a97a8]">No staff yet.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
      <div className="w-10 h-10 rounded-xl bg-[#eefafa] flex items-center justify-center mb-3"><Icon className="text-[#2ec9c9]" size={20} /></div>
      <p className="text-[#8a97a8] text-sm">{label}</p>
      <p className="text-2xl font-bold text-[#16243b] mt-1">{value}</p>
    </div>
  );
}

function AdminField({ label, value, onChange, type = "text", placeholder, mono, isImage, testid }) {
  if (isImage) {
    return <ImageUpload label={label} value={value} onChange={onChange} />;
  }
  const tid = testid || `content-field-${String(label).trim().toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div>
      <label className="block text-sm font-medium text-[#16243b] mb-1.5 capitalize">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={tid}
        className={`w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20 ${mono ? "font-mono text-sm" : ""}`} />
    </div>
  );
}

function IntegrationLogosEditor({ content, setContent }) {
  const raw = content.integration_logos || [];
  const list = raw.map((it) => (typeof it === "string" ? { image: it, url: "" } : it));
  const update = (next) => setContent({ ...content, integration_logos: next });
  return (
    <div className="border-t border-gray-100 pt-5">
      <div className="flex items-center justify-between mb-3">
        <h5 className="font-semibold text-[#16243b] text-sm">Integration logos <span className="text-xs font-normal text-[#8a97a8]">(each links to a URL)</span></h5>
        <button type="button" onClick={() => update([...list, { image: "", url: "" }])} className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={14} /> Add logo</button>
      </div>
      <div className="space-y-3">
        {list.map((it, i) => (
          <div key={i} className="flex items-start gap-2" data-testid={`integration-row-${i}`}>
            <div className="flex-1 space-y-2">
              <ImageUpload value={it.image} compact onChange={(v) => update(list.map((x, xi) => (xi === i ? { ...x, image: v } : x)))} />
              <input value={it.url || ""} placeholder="Link URL (https://…)" data-testid={`integration-url-${i}`} onChange={(e) => update(list.map((x, xi) => (xi === i ? { ...x, url: e.target.value } : x)))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
            </div>
            <button type="button" data-testid={`integration-remove-${i}`} onClick={() => update(list.filter((_, xi) => xi !== i))} className="text-red-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50"><Trash2 size={16} /></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-[#8a97a8]">No logos. Add one above.</p>}
      </div>
    </div>
  );
}

function ImageListEditor({ title, listKey, content, setContent }) {
  const list = content[listKey] || [];
  const update = (next) => setContent({ ...content, [listKey]: next });
  return (
    <div className="border-t border-gray-100 pt-5">
      <div className="flex items-center justify-between mb-3">
        <h5 className="font-semibold text-[#16243b] text-sm">{title}</h5>
        <button type="button" onClick={() => update([...list, ""])} className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={14} /> Add image</button>
      </div>
      <div className="space-y-2">
        {list.map((src, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`${listKey}-row-${i}`}>
            <div className="flex-1">
              <ImageUpload value={src} compact
                onChange={(v) => update(list.map((x, xi) => (xi === i ? v : x)))} />
            </div>
            <button type="button" data-testid={`${listKey}-remove-${i}`} title="Remove"
              onClick={() => update(list.filter((_, xi) => xi !== i))}
              className="text-red-400 hover:text-red-600 shrink-0 p-2 rounded-lg hover:bg-red-50">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-[#8a97a8]">No images. Add one above.</p>}
      </div>
    </div>
  );
}

function CaseStudiesEditor({ content, setContent }) {
  const list = content.case_studies || [];
  const update = (next) => setContent({ ...content, case_studies: next });
  const setItem = (i, item) => update(list.map((x, xi) => (xi === i ? item : x)));
  const field = (i, key, ph) => (
    <input value={list[i][key] || ""} onChange={(e) => setItem(i, { ...list[i], [key]: e.target.value })} placeholder={ph}
      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
  );
  return (
    <div className="border-t border-gray-100 pt-5">
      <div className="flex items-center justify-between mb-3">
        <h5 className="font-semibold text-[#16243b] text-sm">Client case studies</h5>
        <button type="button" onClick={() => update([...list, { image: "", stat: "", statLabel: "", quote: "", name: "", role: "" }])} className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={14} /> Add</button>
      </div>
      <div className="space-y-4">
        {list.map((c, i) => (
          <div key={i} className="bg-[#f6f8fb] rounded-xl p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-[#8a97a8]">Case study {i + 1}</span>
              <button type="button" onClick={() => update(list.filter((_, xi) => xi !== i))} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
            </div>
            {field(i, "image", "Image URL")}
            <div className="grid grid-cols-2 gap-2">{field(i, "stat", "Stat e.g. 35%")}{field(i, "statLabel", "Stat label")}</div>
            {field(i, "quote", "Quote")}
            <div className="grid grid-cols-2 gap-2">{field(i, "name", "Name")}{field(i, "role", "Role")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArticlesTab({ articles, setArticles }) {
  const [modal, setModal] = React.useState(null);
  const [drag, setDrag] = React.useState(null);
  const load = () => api.get("/articles").then(({ data }) => setArticles(data));
  const save = async (e) => {
    e.preventDefault();
    try {
      if (modal.id) await api.put(`/admin/articles/${modal.id}`, modal);
      else await api.post("/admin/articles", modal);
      toast({ title: "Saved", description: "Article saved." });
      setModal(null); load();
    } catch (err) { toast({ title: "Error", description: "Failed to save", variant: "destructive" }); }
  };
  const del = async (id) => { await api.delete(`/admin/articles/${id}`); toast({ title: "Deleted" }); load(); };
  const onDrop = async (i) => {
    if (drag === null || drag === i) { setDrag(null); return; }
    const next = reorder(articles, drag, i);
    setArticles(next); setDrag(null);
    await api.post("/admin/articles/reorder", { ids: next.map((a) => a.id) });
    toast({ title: "Order saved" });
  };
  if (!articles) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#8a97a8]">Drag cards to reorder how they appear on the home page.</p>
        <button onClick={() => setModal({ tag: "Articles", image: "", title: "", excerpt: "" })} className="gp-btn-teal rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2"><Plus size={18} /> New article</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {articles.map((a, i) => (
          <div key={a.id}
            draggable
            onDragStart={() => setDrag(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            className={`bg-white rounded-2xl overflow-hidden shadow-[0_8px_24px_rgba(20,30,55,0.06)] ${drag === i ? "opacity-50" : ""}`}>
            <div className="h-36 bg-gray-100 relative">
              {a.image && <img src={a.image} alt={a.title} className="w-full h-full object-cover" />}
              <span className="absolute top-2 left-2 bg-white/90 rounded-md p-1 text-[#5a6b80] cursor-grab"><GripVertical size={14} /></span>
            </div>
            <div className="p-4">
              <span className="text-[#2ec9c9] text-xs font-semibold uppercase">{a.tag}</span>
              <h3 className="font-semibold text-[#16243b] mt-1 line-clamp-2">{a.title}</h3>
              <p className="text-sm text-[#8a97a8] mt-1 line-clamp-2">{a.excerpt}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setModal(a)} className="flex-1 gp-btn-outline rounded-lg py-2 text-sm font-medium">Edit</button>
                <button onClick={() => del(a.id)} className="px-3 rounded-lg border border-red-200 text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
        {articles.length === 0 && <p className="text-[#8a97a8]">No articles yet.</p>}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-[460px]" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100 font-bold text-[#16243b]">{modal.id ? "Edit article" : "New article"}</div>
            <form onSubmit={save} className="p-5 space-y-3">
              <input value={modal.title} onChange={(e) => setModal({ ...modal, title: e.target.value })} required placeholder="Title" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              <input value={modal.tag} onChange={(e) => setModal({ ...modal, tag: e.target.value })} placeholder="Tag" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              <ImageUpload label="Image" value={modal.image} onChange={(v) => setModal({ ...modal, image: v })} />
              <textarea value={modal.excerpt} onChange={(e) => setModal({ ...modal, excerpt: e.target.value })} rows={3} placeholder="Excerpt" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] resize-none" />
              <div className="flex gap-2">
                <button type="button" onClick={() => setModal(null)} className="flex-1 gp-btn-outline rounded-xl py-3 font-medium">Cancel</button>
                <button className="flex-1 gp-btn-teal rounded-xl py-3 font-medium">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function LinkRow({ link, onChange, onRemove, dragProps }) {
  return (
    <div className="flex gap-2 items-center bg-white" {...(dragProps || {})}>
      <span className="cursor-grab text-[#c0c8d2] shrink-0" title="Drag to reorder"><GripVertical size={16} /></span>
      <input value={link.label} onChange={(e) => onChange({ ...link, label: e.target.value })} placeholder="Label"
        className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
      <input value={link.url} onChange={(e) => onChange({ ...link, url: e.target.value })} placeholder="URL (e.g. #pricing)"
        className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
      <button type="button" onClick={onRemove} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 size={16} /></button>
    </div>
  );
}

function reorder(list, from, to) {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function HeaderLinksEditor({ content, setContent }) {
  const links = content.header_links || [];
  const update = (next) => setContent({ ...content, header_links: next });
  const [drag, setDrag] = React.useState(null);
  return (
    <div className="border-t border-gray-100 pt-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-[#16243b]">Header menu <span className="text-xs font-normal text-[#8a97a8]">(drag to reorder)</span></h4>
        <button type="button" onClick={() => update([...links, { label: "New link", url: "#" }])}
          className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={14} /> Add link</button>
      </div>
      <div className="space-y-2">
        {links.map((l, i) => (
          <LinkRow key={i} link={l}
            dragProps={{
              draggable: true,
              onDragStart: () => setDrag(i),
              onDragOver: (e) => e.preventDefault(),
              onDrop: () => { if (drag !== null && drag !== i) update(reorder(links, drag, i)); setDrag(null); },
              className: `flex gap-2 items-center bg-white rounded-lg ${drag === i ? "opacity-50" : ""}`,
            }}
            onChange={(nl) => update(links.map((x, xi) => (xi === i ? nl : x)))}
            onRemove={() => update(links.filter((_, xi) => xi !== i))} />
        ))}
        {links.length === 0 && <p className="text-sm text-[#8a97a8]">No menu links. Add one above.</p>}
      </div>
    </div>
  );
}

const SOCIAL_PLATFORMS = ["linkedin", "twitter", "facebook", "instagram", "youtube"];

function ColorField({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
        data-testid={`color-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer shrink-0 bg-white" />
      <div className="flex-1">
        <label className="block text-xs font-medium text-[#16243b] mb-0.5">{label}</label>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="#000000"
          className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" />
      </div>
    </div>
  );
}

function DesignEditor({ content, setContent }) {
  const set = (k) => (v) => setContent({ ...content, [k]: v });
  return (
    <div className="border-t border-gray-100 pt-5" data-testid="design-editor">
      <h5 className="font-semibold text-[#16243b] text-sm mb-1">Design system</h5>
      <p className="text-xs text-[#8a97a8] mb-3">Control the site-wide colours and page background. Applies to buttons, links, headings and accents.</p>
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <ColorField label="Primary colour" value={content.primary_color} onChange={set("primary_color")} />
        <ColorField label="Accent colour" value={content.accent_color} onChange={set("accent_color")} />
        <ColorField label="Footer background" value={content.footer_bg_color} onChange={set("footer_bg_color")} />
      </div>
      <ImageUpload label="Page background image (optional)" value={content.background_image} onChange={set("background_image")} />
    </div>
  );
}

const LEGAL_PAGES = [
  { key: "legal_privacy", name: "Privacy policy" },
  { key: "legal_terms", name: "Terms & conditions" },
  { key: "legal_cookies", name: "Cookie policy" },
  { key: "legal_contact", name: "Contact" },
];

function LegalEditor({ content, setContent }) {
  const setItem = (key, patch) => setContent({ ...content, [key]: { ...(content[key] || {}), ...patch } });
  return (
    <div className="border-t border-gray-100 pt-5" data-testid="legal-editor">
      <h5 className="font-semibold text-[#16243b] text-sm mb-1">Legal &amp; contact pages</h5>
      <p className="text-xs text-[#8a97a8] mb-3">These open as pop-ups from the footer. Edit the title and content of each.</p>
      <div className="space-y-4">
        {LEGAL_PAGES.map(({ key, name }) => {
          const item = content[key] || { title: "", body: "" };
          return (
            <div key={key} className="bg-[#f6f8fb] rounded-xl p-4 space-y-2" data-testid={`legal-item-${key}`}>
              <p className="text-xs font-semibold text-[#8a97a8] uppercase tracking-wide">{name}</p>
              <input value={item.title || ""} onChange={(e) => setItem(key, { title: e.target.value })} placeholder="Title"
                data-testid={`legal-title-${key}`}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
              <textarea value={item.body || ""} onChange={(e) => setItem(key, { body: e.target.value })} rows={6} placeholder="Content"
                data-testid={`legal-body-${key}`}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9] resize-y" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SocialLinksEditor({ content, setContent }) {
  const links = content.social_links || [];
  const update = (next) => setContent({ ...content, social_links: next });
  return (
    <div className="border-t border-gray-100 pt-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-[#16243b]">Social media icons <span className="text-xs font-normal text-[#8a97a8]">(shown in the footer)</span></h4>
        <button type="button" data-testid="add-social-btn" onClick={() => update([...links, { platform: "linkedin", url: "" }])}
          className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={14} /> Add icon</button>
      </div>
      <div className="space-y-2">
        {links.map((s, i) => (
          <div key={i} className="flex gap-2 items-center" data-testid={`social-row-${i}`}>
            <select value={s.platform || "linkedin"} data-testid={`social-platform-${i}`}
              onChange={(e) => update(links.map((x, xi) => (xi === i ? { ...x, platform: e.target.value } : x)))}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9] capitalize bg-white">
              {SOCIAL_PLATFORMS.map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}
            </select>
            <input value={s.url || ""} placeholder="https://…" data-testid={`social-url-${i}`}
              onChange={(e) => update(links.map((x, xi) => (xi === i ? { ...x, url: e.target.value } : x)))}
              className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
            <button type="button" onClick={() => update(links.filter((_, xi) => xi !== i))} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 size={16} /></button>
          </div>
        ))}
        {links.length === 0 && <p className="text-sm text-[#8a97a8]">No social icons. Add one above.</p>}
      </div>
    </div>
  );
}

function FooterEditor({ content, setContent }) {
  const cols = content.footer_columns || [];
  const update = (next) => setContent({ ...content, footer_columns: next });
  const setCol = (ci, col) => update(cols.map((c, i) => (i === ci ? col : c)));
  return (
    <div className="border-t border-gray-100 pt-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-[#16243b]">Footer menu</h4>
        <button type="button" onClick={() => update([...cols, { title: "New column", links: [] }])}
          className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={14} /> Add column</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {cols.map((col, ci) => (
          <div key={ci} className="bg-[#f6f8fb] rounded-xl p-4">
            <div className="flex gap-2 items-center mb-3">
              <input value={col.title} onChange={(e) => setCol(ci, { ...col, title: e.target.value })} placeholder="Column title"
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium outline-none focus:border-[#2ec9c9]" />
              <button type="button" onClick={() => update(cols.filter((_, i) => i !== ci))} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
            </div>
            <div className="space-y-2">
              {(col.links || []).map((l, li) => (
                <LinkRow key={li} link={l}
                  onChange={(nl) => setCol(ci, { ...col, links: col.links.map((x, xi) => (xi === li ? nl : x)) })}
                  onRemove={() => setCol(ci, { ...col, links: col.links.filter((_, xi) => xi !== li) })} />
              ))}
            </div>
            <button type="button" onClick={() => setCol(ci, { ...col, links: [...(col.links || []), { label: "New link", url: "#" }] })}
              className="text-[#1fb0b0] text-xs font-medium mt-2 flex items-center gap-1"><Plus size={13} /> Add link</button>
          </div>
        ))}
      </div>
    </div>
  );
}
